<template>
  <div class="summary-bar-mobile" data-testid="summary-bar-mobile">
    <Teleport to="body">
      <Transition name="fade">
        <div v-if="isSuccessMode" class="summary-bar-overlay-container">
          <button class="summary-bar-overlay" aria-label="Close order summary" @click="handleClose"></button>
        </div>
      </Transition>
    </Teleport>

    <Transition name="slide-switch">
      <div v-if="hasSelections && !isSuccessMode" key="selection-wrapper" class="summary-bar-wrapper center-shadow">
        <SummaryBarSelectionMobile
          :is-collapsed="isCollapsed"
          @success="onSuccess"
          @remove-all="onRemoveAll"
          @toggle-collapse="isCollapsed = !isCollapsed"
        />
      </div>
      <div v-else-if="isSuccessMode" key="success-wrapper" class="summary-bar-wrapper center-shadow">
        <SummaryBarSuccess class="padding-xs" @close="handleClose" />
      </div>
    </Transition>

    <Toast v-if="undoToastVisible" placement="bottom" :duration="4000" class="undo-toast" @destroy="undoToastVisible = false">
      <div class="undo-toast-content">
        <span class="body medium white">{{ undoToastMessage }}</span>
        <button class="undo-btn" :disabled="multiATBStore.isLoading" @click="handleUndo">Undo</button>
      </div>
    </Toast>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useWindowScroll, useScrollLock } from '@vueuse/core';

import SummaryBarSuccess from './SummaryBarSuccess.vue';
import SummaryBarSelectionMobile from './SummaryBarSelectionMobile.vue';
import Toast from '@atomic-ui/toast/ToastRenderer.vue';

import { useCollectionSelectionStore, type SelectedItem } from '~/stores/useCollectionSelectionStore';
import { useMultiATBStore } from '~/stores/useMultiATBStore';
import { pluralize } from '~/utils/string';

// Constants
const SCROLL_THRESHOLD = 50;

const selectionStore = useCollectionSelectionStore();
const { hasSelections, totalQuantity } = storeToRefs(selectionStore);
const multiATBStore = useMultiATBStore();

const isSuccessMode = ref(false);
const isLocked = useScrollLock(computed(() => (typeof document !== 'undefined' ? document.body : null)));

watch(isSuccessMode, (val) => {
  isLocked.value = val;
});

const isCollapsed = ref(false);

const undoToastVisible = ref(false);
const undoToastMessage = ref('');
const savedSelectionsForUndo = ref<SelectedItem[]>([]);

const onRemoveAll = (items: SelectedItem[]) => {
  savedSelectionsForUndo.value = items;
  const totalQtyRemoved = items.reduce((sum, item) => sum + item.quantity, 0);
  undoToastMessage.value = `${totalQtyRemoved} item${pluralize(totalQtyRemoved)} removed`;
  undoToastVisible.value = true;
};

const handleUndo = () => {
  if (multiATBStore.isLoading) return;
  savedSelectionsForUndo.value.forEach((item) => {
    selectionStore.updateSelection(item.productId, item.upcId, {
      quantity: item.quantity,
      upcNumber: item.upcNumber,
      price: item.price,
      imageUrl: item.imageUrl,
      alt: item.alt,
    });
  });
  undoToastVisible.value = false;
};

watch(totalQuantity, (newQty) => {
  if (isSuccessMode.value && newQty > 0) {
    isSuccessMode.value = false;
  }
  isCollapsed.value = false;
});

const onSuccess = () => {
  isSuccessMode.value = true;
};

const handleClose = () => {
  isSuccessMode.value = false;
};

// Scroll animation logic
const { y } = useWindowScroll();

watch(y, (currentY, oldY) => {
  if (currentY > oldY && currentY > SCROLL_THRESHOLD) {
    isCollapsed.value = true;
  } else if (currentY < oldY) {
    isCollapsed.value = false;
  }
});
</script>

<style lang="scss">
@import '~settingSCSS';

.summary-bar-mobile {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 20;
  display: grid;
  grid-template-areas: 'summary-wrapper';
  align-items: end;
  pointer-events: none; /* Allow clicks to pass through empty space */

  & > * {
    pointer-events: auto; /* Re-enable clicks on content */
  }
}

.summary-bar-overlay-container {
  position: fixed;
  inset: 0;
  z-index: 19;
  pointer-events: none;
}

.summary-bar-overlay {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100dvh;
  background: rgba(0, 0, 0, 0.25);
  border: none;
  margin: 0;
  padding: 0;
  pointer-events: auto;

  /* iOS Safari: Ensure the backdrop covers the entire visible viewport. */
  @supports (-webkit-touch-callout: none) {
    position: absolute;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

<style lang="scss" scoped>
@import '~settingSCSS';

.summary-bar-wrapper {
  grid-area: summary-wrapper;
  width: 100%;
  background: $white;
  box-shadow: $drop-shadow-center;
  z-index: 20;

  @include ios-bottom-bleed(150px);
}

.slide-switch-enter-active,
.slide-switch-leave-active {
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
}

.slide-switch-enter-from,
.slide-switch-leave-to {
  transform: translateY(100%);
}

.undo-toast {
  width: calc(100% - 32px);
  max-width: none;
  left: 50%;
  transform: translateX(-50%);
  padding: rem-calc(20);
  background-color: $overlay-color-2;

  .toast-content {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .undo-toast-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .undo-btn {
    background: none;
    border: none;
    color: inherit;
    font-weight: bold;
    text-decoration: underline;
    @include padding-left-xxs;
    @include padding-right-xxs;
  }
}
</style>


